import { type FC, useId } from "react";

import { Text, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { Tooltip } from "@snapchat/snap-design-system";
import { InformationIcon, Sizes } from "@snapchat/snap-design-system-icons";

import { SubmissionQuotaContext } from "../../../shared/components/submission-quota/SubmissionQuotaContext";
import {
    type QuotaResetEntry,
    SubmissionQuotaResetTooltipContent,
} from "../../../shared/components/submission-quota/SubmissionQuotaResetTooltipContent";
import { useSubmissionQuotas } from "../../../shared/hooks/submission-quotas";

import { QuotaRow } from "./QuotaRow";
import styles from "./SubmissionQuotaQuickView.module.css";
import { AboutSubmissionLimits, LensPlus, NonLensPlus, SubmissionLimits } from "./messages.generated";

export const SubmissionQuotaQuickView: FC = () => {
    const { regular, lensPlus } = useSubmissionQuotas();
    const headingId = useId();
    const entries: QuotaResetEntry[] = [
        ...(regular ? [{ context: SubmissionQuotaContext.Regular, quota: regular }] : []),
        ...(lensPlus ? [{ context: SubmissionQuotaContext.LensPlus, quota: lensPlus }] : []),
    ];

    if (entries.length === 0) {
        return null;
    }

    return (
        <section className={styles.container} aria-labelledby={headingId}>
            <div className={styles.headingRow}>
                <Text component="h2" id={headingId} size="xs" fw={500} c="dimmed" className={styles.heading}>
                    <SubmissionLimits />
                </Text>
                {entries.some((entry) => entry.quota.replenishAt) ? (
                    <Tooltip content={<SubmissionQuotaResetTooltipContent entries={entries} />}>
                        <UnstyledButton type="button" className={styles.infoButton}>
                            <InformationIcon size={Sizes.Small} aria-hidden />
                            <VisuallyHidden>
                                <AboutSubmissionLimits />
                            </VisuallyHidden>
                        </UnstyledButton>
                    </Tooltip>
                ) : null}
            </div>
            <div className={styles.rows}>
                {regular ? (
                    <QuotaRow label={<NonLensPlus />} quota={regular} context={SubmissionQuotaContext.Regular} />
                ) : null}
                {lensPlus ? (
                    <QuotaRow label={<LensPlus />} quota={lensPlus} context={SubmissionQuotaContext.LensPlus} />
                ) : null}
            </div>
        </section>
    );
};
